import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Building2, ChevronsUpDown, Hash, Search, X } from "lucide-react"

interface Merchant {
  id: string
  name: string
  domain: string
}

interface MerchantSelectorProps {
  merchants: Merchant[]
  onMerchantSelect: (merchantId: string) => void
  selectedMerchantForDisplay: string
}

export const MerchantSelector = ({
  merchants,
  onMerchantSelect,
  selectedMerchantForDisplay,
}: MerchantSelectorProps) => {
  const { t } = useTranslation()
  const [searchTerm, setSearchTerm] = useState("")
  const [isOpen, setIsOpen] = useState(false)

  const handleMerchantClick = (merchantId: string) => {
    const merchant = merchants.find((m) => m.id === merchantId)
    if (merchant) onMerchantSelect(`${merchant.name} (${merchant.domain})`)
    setSearchTerm("")
    setIsOpen(false)
  }

  const isFullMerchantId = (input: string) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(input.trim())

  const handleDirectMerchantId = (merchantId: string) => {
    onMerchantSelect(`Merchant ID: ${merchantId}`)
    setSearchTerm("")
    setIsOpen(false)
  }

  const handleClear = () => {
    onMerchantSelect("")
    setSearchTerm("")
  }

  const filteredMerchants = merchants
    .filter(
      (merchant) =>
        !searchTerm ||
        merchant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        merchant.domain.toLowerCase().includes(searchTerm.toLowerCase()) ||
        merchant.id.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .slice(0, 12)

  // Closed state — show selected value as a button
  if (!isOpen)
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(true)}
        className="gap-2 font-normal"
        data-testid="merchant-selector-trigger"
      >
        <Building2 className="size-3.5 text-muted-foreground" />
        <span className="max-w-[180px] truncate">
          {selectedMerchantForDisplay || t("analytics.selectMerchant")}
        </span>
        <ChevronsUpDown className="size-3 text-muted-foreground" />
      </Button>
    )

  // Open state — search + dropdown
  return (
    <div className="relative" data-testid="merchant-selector">
      <div
        className="fixed inset-0 z-10"
        onClick={() => {
          setIsOpen(false)
          setSearchTerm("")
        }}
      />
      <div className="relative z-20">
        <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
        <input
          data-testid="merchant-selector-search"
          autoFocus
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={t("analytics.searchMerchants")}
          className="flex h-8 w-[280px] rounded-md border border-input bg-background px-8 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
        {selectedMerchantForDisplay && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="size-3.5" />
          </button>
        )}
      </div>

      <div className="absolute left-0 top-full z-20 mt-1 w-[280px] rounded-md border bg-popover shadow-lg">
        <div className="max-h-[280px] overflow-y-auto p-1">
          {isFullMerchantId(searchTerm) && (
            <button
              type="button"
              data-testid="merchant-selector-direct-id"
              className="flex w-full flex-col gap-0.5 rounded-sm px-3 py-2 text-left transition-colors hover:bg-accent"
              onClick={() => handleDirectMerchantId(searchTerm.trim())}
            >
              <span className="flex items-center gap-1.5 text-sm font-medium text-primary">
                <Hash className="size-3.5" />
                {t("analytics.searchByMerchantId")}
              </span>
              <span className="truncate font-mono text-xs text-muted-foreground">
                {searchTerm.trim()}
              </span>
            </button>
          )}

          {filteredMerchants.length > 0 ? (
            filteredMerchants.map((merchant) => (
              <button
                type="button"
                key={merchant.id}
                data-testid="merchant-selector-option"
                data-merchant-id={merchant.id}
                className="flex w-full flex-col gap-0.5 rounded-sm px-3 py-2 text-left transition-colors hover:bg-accent"
                onClick={() => handleMerchantClick(merchant.id)}
              >
                <span className="flex items-center gap-1.5 text-sm font-medium">
                  <Building2 className="size-3.5 text-muted-foreground" />
                  {merchant.name}
                </span>
                <span className="truncate pl-5 text-xs text-muted-foreground">
                  {merchant.domain}
                </span>
              </button>
            ))
          ) : (
            <p className="px-3 py-4 text-center text-sm text-muted-foreground">
              No merchants found
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
